/**
 * The shell's words and the words every page shares, by locale: one object
 * per locale, each annotated with the one interface, so a missing or an
 * extra key, or a function of another shape, does not compile; `byLocale`
 * takes them all and `locale()` picks one. A feature keeps its own words in
 * features/<f>/ui/strings.ts, typed the same way. Copy with a value in it is
 * a function, so word order can differ per language.
 */
import { LANGUAGES, type Language } from '@makan/contracts/client';

/** The languages a page is written in (@makan/contracts/client: a command that records one takes the same union). */
export const LOCALES = LANGUAGES;
export type Locale = Language;

export interface KitStrings {
  readonly common: {
    readonly offline: string;
    /** While a call waits to retry (kit/api.ts): calm, no error, no action asked. */
    readonly reconnecting: string;
    /** While a staff page's file loads, in the frame where the page will be. */
    readonly loading: string;
    readonly rateLimited: (minutes: number) => string;
    readonly unexpected: string;
    readonly notFound: string;
    /** A page that failed to draw (kit/boundary.tsx): what the person sees in its place, and the button that loads it again. */
    readonly brokenTitle: string;
    readonly brokenBody: string;
    readonly retry: string;
    /** After a field's label, the word that says it may stay empty: " (optional)". */
    readonly optional: (word: string) => string;
  };
  readonly staff: {
    readonly product: string;
    readonly signInTitle: string;
    readonly emailLabel: string;
    readonly passwordLabel: string;
    readonly signIn: string;
    readonly signingIn: string;
    readonly signInFailed: string;
    readonly signInMissing: string;
    readonly signOut: string;
    /** An old staff link that names a group the signed-in staff member is not in. */
    readonly otherGroup: string;
    readonly otherGroupBody: string;
    readonly toOwnGroup: string;
  };
  /** A phone field's country list. */
  readonly phone: {
    readonly country: string;
    /** The country button's name: "Country: Malaysia +60". */
    readonly countryNamed: (name: string, dial: string) => string;
    readonly search: string;
    readonly noMatch: string;
  };
  /** The staff frame: the sidebar, the top bar, the phone's tab bar and the status bar. */
  readonly shell: {
    readonly menu: string;
    readonly openMenu: string;
    readonly more: string;
    readonly close: string;
    readonly soon: string;
    readonly home: string;
    readonly kiosk: string;
    readonly kitchen: string;
    readonly reports: string;
    readonly settings: string;
    readonly ask: string;
    readonly askSoon: string;
    readonly account: string;
    readonly signedInAs: string;
    readonly online: string;
    readonly offline: string;
    readonly autoCount: string;
    readonly status: string;
    readonly crumbs: string;
    /** The language menu's accessible name; the button shows the language in force, the menu each one in its own words (LANGUAGE_NAMES). */
    readonly switchLanguage: string;
  };
}

const en: KitStrings = {
  common: {
    offline: 'Could not connect. Check your connection and try again.',
    reconnecting: 'Reconnecting…',
    loading: 'Loading…',
    rateLimited: (minutes) => `Too many tries from this network. Try again in ${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}.`,
    unexpected: 'Something went wrong on our side. Please try again.',
    notFound: 'This page does not exist.',
    brokenTitle: 'Something went wrong',
    brokenBody: 'This page could not open. Try again.',
    retry: 'Retry',
    optional: (word) => ` (${word})`,
  },
  staff: {
    product: 'Command center',
    signInTitle: 'Sign in',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in',
    otherGroup: 'This link is for another group',
    otherGroupBody: 'You are signed in to a different group, so this page is not shown.',
    toOwnGroup: 'Go to your group',
    signInFailed: 'Wrong email or password.',
    signInMissing: 'Enter your email and password.',
    signOut: 'Sign out',
  },
  phone: {
    country: 'Country',
    countryNamed: (name, dial) => `Country: ${name} ${dial}`,
    search: 'Search countries',
    noMatch: 'No country matches.',
  },
  shell: {
    menu: 'Menu',
    openMenu: 'Open the menu',
    more: 'More',
    close: 'Close',
    soon: 'Soon',
    home: 'Home',
    kiosk: 'Kiosk',
    kitchen: 'Kitchen',
    reports: 'Reports',
    settings: 'Settings',
    ask: 'Ask or search',
    askSoon: 'Ask or search: coming soon',
    account: 'Account',
    signedInAs: 'Signed in as',
    online: 'Online',
    offline: 'Offline',
    autoCount: 'AutoCount sync',
    status: 'Status',
    crumbs: 'Where you are',
    switchLanguage: 'Switch language',
  },
};


const zh: KitStrings = {
  common: {
    offline: '无法连接。请检查网络后再试。',
    reconnecting: '正在重新连接…',
    loading: '加载中…',
    rateLimited: (minutes) => `这个网络尝试次数太多。请 ${String(minutes)} 分钟后再试。`,
    unexpected: '我们这边出了点问题。请再试一次。',
    notFound: '这个页面不存在。',
    brokenTitle: '出了点问题',
    brokenBody: '这个页面无法打开，请重试。',
    retry: '重试',
    optional: (word) => `（${word}）`,
  },
  staff: {
    product: '管理中心',
    signInTitle: '登录',
    emailLabel: '电邮',
    passwordLabel: '密码',
    signIn: '登录',
    signingIn: '登录中',
    otherGroup: '这个链接属于另一个集团',
    otherGroupBody: '你登录的是另一个集团，所以这个页面不会显示。',
    toOwnGroup: '前往你的集团',
    signInFailed: '电邮或密码不正确。',
    signInMissing: '请输入电邮和密码。',
    signOut: '登出',
  },
  phone: {
    country: '国家',
    countryNamed: (name, dial) => `国家：${name} ${dial}`,
    search: '搜索国家',
    noMatch: '没有符合的国家。',
  },
  shell: {
    menu: '菜单',
    openMenu: '打开菜单',
    more: '更多',
    close: '关闭',
    soon: '即将推出',
    home: '首页',
    kiosk: '自助点餐机',
    kitchen: '厨房',
    reports: '报表',
    settings: '设置',
    ask: '提问或搜索',
    askSoon: '提问或搜索：即将推出',
    account: '账户',
    signedInAs: '已登录账户',
    online: '在线',
    offline: '离线',
    autoCount: 'AutoCount 同步',
    status: '状态',
    crumbs: '当前位置',
    switchLanguage: '切换语言',
  },
};

const ms: KitStrings = {
  common: {
    offline: 'Tidak dapat bersambung. Semak sambungan anda dan cuba lagi.',
    reconnecting: 'Menyambung semula…',
    loading: 'Memuatkan…',
    rateLimited: (minutes) => `Terlalu banyak cubaan dari rangkaian ini. Cuba lagi dalam ${String(minutes)} minit.`,
    unexpected: 'Ada masalah di pihak kami. Sila cuba lagi.',
    notFound: 'Halaman ini tidak wujud.',
    brokenTitle: 'Ada masalah',
    brokenBody: 'Halaman ini tidak dapat dibuka. Cuba lagi.',
    retry: 'Cuba lagi',
    optional: (word) => ` (${word})`,
  },
  staff: {
    product: 'Pusat kawalan',
    signInTitle: 'Log masuk',
    emailLabel: 'E-mel',
    passwordLabel: 'Kata laluan',
    signIn: 'Log masuk',
    signingIn: 'Sedang log masuk',
    signInFailed: 'E-mel atau kata laluan salah.',
    signInMissing: 'Masukkan e-mel dan kata laluan anda.',
    signOut: 'Log keluar',
    otherGroup: 'Pautan ini untuk kumpulan lain',
    otherGroupBody: 'Anda log masuk ke kumpulan lain, jadi halaman ini tidak dipaparkan.',
    toOwnGroup: 'Pergi ke kumpulan anda',
  },
  phone: {
    country: 'Negara',
    countryNamed: (name, dial) => `Negara: ${name} ${dial}`,
    search: 'Cari negara',
    noMatch: 'Tiada negara yang sepadan.',
  },
  shell: {
    menu: 'Menu',
    openMenu: 'Buka menu',
    more: 'Lagi',
    close: 'Tutup',
    soon: 'Akan datang',
    home: 'Utama',
    kiosk: 'Kiosk',
    kitchen: 'Dapur',
    reports: 'Laporan',
    settings: 'Tetapan',
    ask: 'Tanya atau cari',
    askSoon: 'Tanya atau cari: akan datang',
    account: 'Akaun',
    signedInAs: 'Log masuk sebagai',
    online: 'Dalam talian',
    offline: 'Luar talian',
    autoCount: 'Penyegerakan AutoCount',
    status: 'Status',
    crumbs: 'Lokasi anda',
    switchLanguage: 'Tukar bahasa',
  },
};

/** Each language's own name: what the language menu lists. */
export const LANGUAGE_NAMES: Readonly<Record<Locale, string>> = { en: 'English', zh: '中文', ms: 'Bahasa Melayu' };

/** Each language as the menu's button shows it when in force: short enough for a phone's brand band. */
export const LANGUAGE_SHORT: Readonly<Record<Locale, string>> = { en: 'EN', zh: '中文', ms: 'BM' };

/** Where this device keeps an explicit choice of language. */
const SAVED = 'makan.locale';

const isLocale = (value: unknown): value is Locale => LOCALES.some((one) => one === value);

function saved(): Locale | null {
  try {
    const value = localStorage.getItem(SAVED);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/** The page language a browser's language reads as: Chinese of any script or region reads zh, Malay ms, anything else en. */
function ofBrowser(language: string): Locale {
  const primary = language.toLowerCase().split('-')[0];
  return primary === 'zh' ? 'zh' : primary === 'ms' ? 'ms' : 'en';
}

/** The choice saved on this device, else the page's `?lang=`, else the browser's language, else en. */
function resolve(): Locale {
  if (typeof window === 'undefined') return 'en';
  const asked = new URLSearchParams(location.search).get('lang');
  return saved() ?? (isLocale(asked) ? asked : null) ?? ofBrowser(navigator.language);
}

let inForce: Locale | undefined;

/** The locale in force, read once per page load: every strings object is picked at module load, so switching reloads the page (switchLocale). */
export function locale(): Locale {
  inForce ??= resolve();
  return inForce;
}

/** The `<html lang>` of the locale in force: zh is written in Simplified Chinese. */
export function htmlLang(): string {
  return locale() === 'zh' ? 'zh-Hans' : locale();
}

/**
 * Saves the choice on this device and reloads in it. Saved, the page's
 * `?lang=` is dropped (the choice outranks it); when the device keeps
 * nothing (a private window), `?lang=` carries the choice instead.
 */
export function switchLocale(to: Locale): void {
  const url = new URL(location.href);
  try {
    localStorage.setItem(SAVED, to);
    url.searchParams.delete('lang');
  } catch {
    url.searchParams.set('lang', to);
  }
  if (url.href === location.href) location.reload();
  else location.replace(url.href);
}

/** Picks the strings of the locale in force from one object per locale: a feature's strings.ts ends with `byLocale({ en, zh, ms })`. */
export function byLocale<S>(strings: Readonly<Record<Locale, S>>): S {
  return strings[locale()];
}

export const strings: KitStrings = byLocale({ en, zh, ms });
